import React, { useState, useEffect } from 'react';
import { 
  Typography, Container, Paper, Button, Box, IconButton, Tooltip,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar,
  ToggleButton, ToggleButtonGroup, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getUserAttendance, getAllAttendance, checkIn, checkOut, markAbsent, startLunch, endLunch, startBreak, endBreak } from '../../api/attendanceAPI';
import { getUserLeaves } from '../../api/leaveAPI';
import { getAllHolidays } from '../../api/holidayAPI';
import { getAllUsers } from '../../api/userAPI';
import moment from 'moment';

const Attendance = () => {
  const { user } = useSelector((state) => state.tokens);
  
  const [attendance, setAttendance] = useState([]);
  const [allAttendance, setAllAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [attendanceRange, setAttendanceRange] = useState('today');
  const [specificDate, setSpecificDate] = useState(moment().format('YYYY-MM-DD'));
  const isAdmin = user?.permission === 'Admin' || user?.permission === 'HR Management';
  
  const [currentDate, setCurrentDate] = useState(moment());
  const [openReason, setOpenReason] = useState(false);
  const [reason, setReason] = useState("");

  const [openAbsent, setOpenAbsent] = useState(false);
  const [absentUser, setAbsentUser] = useState("");
  const [absentDate, setAbsentDate] = useState(moment().format('YYYY-MM-DD'));
  const [usersList, setUsersList] = useState([]);

  const fetchData = async () => {
    try {
      if(!user || !user._id) return;
      const attRes = await getUserAttendance(user._id);
      if(attRes.data && attRes.data.success) {
        setAttendance(attRes.data.log);
      }
      const leaveRes = await getUserLeaves(user._id);
      if(leaveRes.data && leaveRes.data.success) {
        setLeaves(leaveRes.data.leaves);
      }
      const holRes = await getAllHolidays();
      if(holRes.data && holRes.data.success) {
        setHolidays(holRes.data.holidays);
      }
    } catch(err) {
      console.error(err);
    }
  };

  const fetchTeamAttendance = async () => {
    try {
      const params = attendanceRange === 'custom' 
        ? { date: specificDate } 
        : { range: attendanceRange };
      const res = await getAllAttendance(params);
      if(res.data && res.data.success) {
        setAllAttendance(res.data.log);
      }
    } catch(err) { console.error(err); }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  useEffect(() => {
    if(isAdmin && activeTab === 1) {
      fetchTeamAttendance();
      if(usersList.length === 0) {
        getAllUsers().then(res => {
          if(res.data) {
            const activeOfficeEmps = res.data.filter(u => u.isActive && u.isOfficeEmp);
            setUsersList(activeOfficeEmps);
          }
        }).catch(err => console.error(err));
      }
    }
  }, [isAdmin, activeTab, attendanceRange, specificDate]);

  const handleCheckIn = async () => {
    try {
      const todayStr = moment().format('YYYY-MM-DD');
      await checkIn({ user: user._id, date: todayStr });
      fetchData();
    } catch(err) {
      console.error(err);
    }
  };

  const handleCheckOut = async () => {
    try {
      const now = moment();
      const hours = now.hours();
      const minutes = now.minutes();
      const totalMinutes = hours * 60 + minutes;
      const startLimit = 13 * 60 + 30; // 1:30 PM
      const endLimit = 17 * 60 + 30;   // 5:30 PM

      if (totalMinutes >= startLimit && totalMinutes < endLimit && !reason) {
        setOpenReason(true);
        return;
      }

      const todayStr = moment().format('YYYY-MM-DD');
      await checkOut({ user: user._id, date: todayStr, earlyCheckoutReason: reason });
      setOpenReason(false);
      setReason("");
      fetchData();
    } catch(err) {
      console.error(err);
    }
  };

  const handleStartLunch = async () => {
    try {
      const todayStr = moment().format('YYYY-MM-DD');
      await startLunch({ user: user._id, date: todayStr });
      fetchData();
    } catch(err) {
      console.error(err);
      alert(err.response?.data?.message || "Error starting lunch");
    }
  };

  const handleEndLunch = async () => {
    try {
      const todayStr = moment().format('YYYY-MM-DD');
      await endLunch({ user: user._id, date: todayStr });
      fetchData();
    } catch(err) {
      console.error(err);
      alert(err.response?.data?.message || "Error ending lunch");
    }
  };

  const handleStartBreak = async () => {
    try {
      const todayStr = moment().format('YYYY-MM-DD');
      await startBreak({ user: user._id, date: todayStr });
      fetchData();
    } catch(err) {
      console.error(err);
      alert(err.response?.data?.message || "Error starting break");
    }
  };

  const handleEndBreak = async () => {
    try {
      const todayStr = moment().format('YYYY-MM-DD');
      await endBreak({ user: user._id, date: todayStr });
      fetchData();
    } catch(err) {
      console.error(err);
      alert(err.response?.data?.message || "Error ending break");
    }
  };

  const handleMarkAbsent = async () => {
    if(!absentUser || !absentDate) return;
    try {
      await markAbsent({ user: absentUser, date: absentDate });
      setOpenAbsent(false);
      setAbsentUser("");
      fetchTeamAttendance();
    } catch(err) {
      console.error(err);
    }
  };

  const formatTime = (totalSeconds) => {
    if(!totalSeconds) return '00:00:00';
    const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const s = (totalSeconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const getTotalBreakTime = (breaks) => {
    if (!breaks || breaks.length === 0) return 0;
    let total = 0;
    breaks.forEach(b => {
      if (b.end) {
        total += moment(b.end).diff(moment(b.start), 'seconds');
      } else {
        total += moment().diff(moment(b.start), 'seconds');
      }
    });
    return total;
  };
  
  // Calendar Rendering Logic
  const startDay = currentDate.clone().startOf('month').startOf('week');
  const endDay = currentDate.clone().endOf('month').endOf('week');
  const day = startDay.clone().subtract(1, 'day');
  const calendar = [];
  while (day.isBefore(endDay, 'day')) {
    calendar.push(
      Array(7).fill(0).map(() => day.add(1, 'day').clone())
    );
  }

  // Check if today has attendance
  const todayStr = moment().format('YYYY-MM-DD');
  const isTodaySunday = moment().day() === 0;
  
  // Check if today is a company holiday
  const isTodayHoliday = holidays.some(h => moment(h.date).format('YYYY-MM-DD') === todayStr);

  const todayAtt = attendance.find(a => a.date === todayStr);
  const isBreakActive = todayAtt?.shortBreaks?.length > 0 && !todayAtt.shortBreaks[todayAtt.shortBreaks.length - 1].end;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">Attendance Management</Typography>
        <Box>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ mr: 2 }} 
            onClick={handleCheckIn}
            disabled={!!todayAtt || isTodaySunday || isTodayHoliday}
          >
            Check In
          </Button>
          <Button 
            variant="contained" 
            color="warning" 
            sx={{ mr: 2 }} 
            onClick={handleStartLunch}
            disabled={!todayAtt || !!todayAtt.lunchStart || !!todayAtt.checkOut}
          >
            Start Lunch
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            sx={{ mr: 2 }} 
            onClick={handleEndLunch}
            disabled={!todayAtt || !todayAtt.lunchStart || !!todayAtt.lunchEnd || !!todayAtt.checkOut}
          >
            End Lunch
          </Button>
          <Button 
            variant="contained" 
            color="info" 
            sx={{ mr: 2 }} 
            onClick={handleStartBreak}
            disabled={!todayAtt || !!todayAtt.checkOut || (!!todayAtt.lunchStart && !todayAtt.lunchEnd) || isBreakActive}
          >
            Start Break
          </Button>
          <Button 
            variant="contained" 
            sx={{ mr: 2, bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }} 
            onClick={handleEndBreak}
            disabled={!todayAtt || !!todayAtt.checkOut || !isBreakActive}
          >
            End Break
          </Button>
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={handleCheckOut}
            disabled={!todayAtt || !!todayAtt.checkOut}
          >
            Check Out
          </Button>
        </Box>
      </Box>

      {isAdmin && (
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ mb: 2 }}>
          <Tab label="My Attendance" />
          <Tab label="Team Attendance" />
        </Tabs>
      )}

      {/* MY ATTENDANCE CALENDAR TAB */}
      {activeTab === 0 && (
        <>
          <Paper sx={{ p: 2, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={() => setCurrentDate(currentDate.clone().subtract(1, 'month'))}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="h5" fontWeight="medium">
                {currentDate.format('MMMM YYYY')}
              </Typography>
              <IconButton onClick={() => setCurrentDate(currentDate.clone().add(1, 'month'))}>
                <ChevronRight />
              </IconButton>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <Box key={d} sx={{ textAlign: 'center', fontWeight: 'bold', pb: 1, color: d === 'Sun' ? 'error.main' : 'inherit' }}>
                  {d}
                </Box>
              ))}
              {calendar.flat().map((dayItem, index) => {
                const dateStr = dayItem.format('YYYY-MM-DD');
                const isCurrentMonth = dayItem.month() === currentDate.month();
                const isSunday = dayItem.day() === 0;
                
                // Company Holiday on that day
                const dayHolidays = holidays.filter(h => moment(h.date).format('YYYY-MM-DD') === dateStr);
                const isHoliday = isSunday || dayHolidays.length > 0;
                
                // Attendance on that day
                const dayAtt = attendance.find(a => a.date === dateStr);
                
                // Leave covering that day
                const dayLeave = leaves.filter(l => {
                  if (l.dates && l.dates.length > 0) {
                     return l.dates.some(d => moment(d).format('YYYY-MM-DD') === dateStr);
                  }
                  if (l.startDate && l.endDate) {
                     const s = moment(l.startDate).startOf('day');
                     const e = moment(l.endDate).endOf('day');
                     return dayItem.isBetween(s, e, 'day', '[]');
                  }
                  return false;
                });

                return (
                  <Box 
                    key={index}
                    sx={{
                      minHeight: 100,
                      p: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      bgcolor: isHoliday ? '#ffebee' : (isCurrentMonth ? '#fff' : '#f5f5f5'),
                      color: isCurrentMonth ? 'text.primary' : 'text.disabled',
                      transition: '0.2s',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': { bgcolor: isHoliday ? '#ffcdd2' : '#f0f4f8' }
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      fontWeight={dayItem.isSame(moment(), 'day') ? 'bold' : 'normal'} 
                      color={dayItem.isSame(moment(), 'day') ? 'primary' : (isHoliday && isCurrentMonth ? 'error.main' : 'inherit')}
                    >
                      {dayItem.date()}
                    </Typography>
                    
                    {/* Render Holiday Label */}
                    {isHoliday && isCurrentMonth && (
                      <Box sx={{
                        position: 'absolute', top: 5, right: 5, 
                        fontSize: '0.5rem', fontWeight: 'bold', color: 'error.main', opacity: 0.7,
                        textAlign: 'right'
                      }}>
                        {isSunday ? 'SUNDAY' : (dayHolidays[0]?.name?.toUpperCase() || 'HOLIDAY')}
                      </Box>
                    )}

                    {/* Render Attendance */}
                    {dayAtt && (
                      <Tooltip title={`Check In: ${dayAtt.checkIn ? moment(dayAtt.checkIn).format('hh:mm A') : 'N/A'}\nCheck Out: ${dayAtt.checkOut ? moment(dayAtt.checkOut).format('hh:mm A') : 'Pending'}`}>
                        <Box sx={{
                          mt: 1, p: 0.5, bgcolor: '#4caf50', color: '#fff', borderRadius: 1,
                          fontSize: '0.75rem', textAlign: 'center', cursor: 'pointer'
                        }}>
                          Present
                        </Box>
                      </Tooltip>
                    )}

                    {/* Render Leaves */}
                    {dayLeave.map((l) => (
                      <Tooltip title={`Reason: ${l.reason || 'N/A'} - Status: ${l.status}`} key={l._id}>
                        <Box sx={{
                          mt: 0.5, p: 0.5, 
                          bgcolor: l.status === 'Approved' ? '#2196f3' : (l.status === 'Rejected' ? '#f44336' : '#ff9800'),
                          color: '#fff', borderRadius: 1, fontSize: '0.75rem', textAlign: 'center', cursor: 'pointer',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}>
                          {l.status === 'Approved' ? `On Leave (${l.type})` : `Leave ${l.status}`}
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </>
      )}

      {/* TEAM ATTENDANCE LOG TAB */}
      {isAdmin && activeTab === 1 && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {attendanceRange === 'custom' 
                ? `Attendance for ${moment(specificDate).format('DD MMM YYYY')}` 
                : `${attendanceRange.charAt(0).toUpperCase() + attendanceRange.slice(1)} Log`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button variant="contained" color="error" size="small" onClick={() => setOpenAbsent(true)}>
                Mark Absent
              </Button>
              {attendanceRange === 'custom' && (
                <TextField
                  type="date"
                  size="small"
                  value={specificDate}
                  onChange={(e) => setSpecificDate(e.target.value)}
                  sx={{ width: 150 }}
                />
              )}
              <ToggleButtonGroup
                size="small"
                color="primary"
                value={attendanceRange}
                exclusive
                onChange={(e, val) => val && setAttendanceRange(val)}
              >
                <ToggleButton value="today" sx={{ px: 2 }}>Today</ToggleButton>
                <ToggleButton value="custom" sx={{ px: 2 }}>Specific Day</ToggleButton>
                <ToggleButton value="weekly" sx={{ px: 2 }}>Weekly</ToggleButton>
                <ToggleButton value="monthly" sx={{ px: 2 }}>Monthly</ToggleButton>
                <ToggleButton value="yearly" sx={{ px: 2 }}>Yearly</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell><b>User</b></TableCell>
                  <TableCell><b>Date</b></TableCell>
                  <TableCell><b>Check In</b></TableCell>
                  <TableCell><b>Check Out</b></TableCell>
                  <TableCell><b>Lunch</b></TableCell>
                  <TableCell><b>Breaks</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allAttendance.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center">No team attendance logs found.</TableCell></TableRow>
                ) : allAttendance.map((log) => (
                  <TableRow key={log._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32, fontSize: '0.9rem' }}>
                          {log.user?.fullName?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">{log.user?.fullName || 'Unknown'}</Typography>
                          <Typography variant="caption" color="text.secondary">{log.user?.email}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{moment(log.date).format('DD MMM YYYY')}</TableCell>
                    <TableCell>{log.checkIn ? moment(log.checkIn).format('hh:mm A') : '-'}</TableCell>
                    <TableCell>{log.checkOut ? moment(log.checkOut).format('hh:mm A') : '-'}</TableCell>
                    <TableCell>
                      {log.lunchStart ? (
                        <Box>
                          <Typography variant="caption" display="block">Start: {moment(log.lunchStart).format('hh:mm A')}</Typography>
                          {log.lunchEnd ? (
                            <Typography variant="caption" display="block">End: {moment(log.lunchEnd).format('hh:mm A')}</Typography>
                          ) : (
                            <Typography variant="caption" display="block" color="warning.main">In Progress</Typography>
                          )}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {log.shortBreaks && log.shortBreaks.length > 0 ? (
                        <Box>
                          {log.shortBreaks.map((b, i) => (
                            <Typography key={i} variant="caption" display="block">
                              {moment(b.start).format('hh:mm A')} - {b.end ? moment(b.end).format('hh:mm A') : 'Ongoing'}
                            </Typography>
                          ))}
                          <Typography variant="caption" fontWeight="bold" display="block" color="secondary" sx={{ mt: 0.5 }}>
                            Total: {formatTime(getTotalBreakTime(log.shortBreaks))}
                          </Typography>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ 
                        px: 1.5, py: 0.5, borderRadius: 10, 
                        bgcolor: log.status === 'Absent' ? '#ffebee' : (log.status === 'Half-day' ? '#fff3e0' : '#e8f5e9'), 
                        color: log.status === 'Absent' ? '#c62828' : (log.status === 'Half-day' ? '#e65100' : '#2e7d32'), 
                        display: 'inline-block', fontSize: '0.75rem', fontWeight: 'bold' 
                      }}>
                        {log.status || 'Present'}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={openAbsent} onClose={() => setOpenAbsent(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark Full Day Absent</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>User</InputLabel>
            <Select
              value={absentUser}
              label="User"
              onChange={(e) => setAbsentUser(e.target.value)}
            >
              {usersList.map((u) => (
                <MenuItem key={u.id || u._id} value={u.id || u._id}>{u.fullName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Date"
            type="date"
            fullWidth
            value={absentDate}
            onChange={(e) => setAbsentDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAbsent(false)}>Cancel</Button>
          <Button 
            onClick={handleMarkAbsent} 
            color="error" 
            variant="contained" 
            disabled={!absentUser || !absentDate}
          >
            Mark Absent
          </Button>
        </DialogActions>
      </Dialog>

      {/* EARLY CHECKOUT REASON DIALOG */}
      <Dialog open={openReason} onClose={() => setOpenReason(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Early Check Out Reason</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            You are checking out before 5:30 PM. Please provide a reason for the Admin's review.
          </Typography>
          <TextField
            autoFocus
            label="Reason for early checkout"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReason(false)}>Cancel</Button>
          <Button 
            onClick={handleCheckOut} 
            color="error" 
            variant="contained" 
            disabled={!reason.trim()}
          >
            Confirm Check Out
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Attendance;
